"use client";
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import chat_explore from "../../assets/chat_explore.png";
import { REST_API_URL } from "../../api/config";
const API_BASE = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function CommunityMain({ onJoined }) {
  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  const endpoints = {
    getMessagingCommunities: "/community/communities/",
    postCommunityJoin: "/community/community-join/",
    postCommunityLeave: "/community/community-leave/",
    getMessagingJoinedCommunities: "/community/joined-communities/",
    getMessagingCommunityMessages: "/community/community-messages/",
    postMessagingCommunityPost: "/community/community-message-post/",
  };

  const [communities, setCommunities] = useState([]);
  const [joinedCommunities, setJoinedCommunities] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("welcome");
  const [dataLoaded, setDataLoaded] = useState(false);

  const messagesEndRef = useRef(null);

  const headers = buildHeaders(token);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (view === "chat") {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, view]);

  const handleExplore = async () => {
    try {
      setLoading(true);
      
      const res = await fetch(`${API_BASE}${endpoints.getMessagingCommunities}`, {
        headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setCommunities(
        (Array.isArray(data) ? data : data.results || []).map((c) => ({
          ...c,
          joined: c.joined === true || c.joined === "true",
        }))
      );
      setDataLoaded(true);
      setView("explore");
    } catch (e) {
      console.error("❌ Error fetching communities:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleMyCommunities = async () => {
    if (dataLoaded) {
      setView("joined");
      return;
    }

    try {
      setLoading(true);
      
      const res = await fetch(`${API_BASE}${endpoints.getMessagingJoinedCommunities}`, {
        headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setJoinedCommunities(
        (Array.isArray(data) ? data : data.results || []).map((c) => ({
          ...c,
          joined: true,
        }))
      );
      setDataLoaded(true);
      setView("joined");
    } catch (e) {
      console.error("❌ Error fetching joined communities:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (id) => {
    try {
      const res = await fetch(`${API_BASE}${endpoints.postCommunityJoin}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ community: id }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setCommunities((prev) =>
        prev.map((c) =>
          c.id === id
            ? { ...c, joined: true, joined_count: (c.joined_count || 0) + 1 }
            : c
        )
      );

      if (onJoined) onJoined();

    } catch (e) {
      console.error("❌ Error joining community:", e);
    }
  };

  const handleLeave = async (id) => {
    try {
      const res = await fetch(`${API_BASE}${endpoints.postCommunityLeave}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ community: id }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setJoinedCommunities((prev) => prev.filter((c) => c.id !== id));
      setCommunities((prev) =>
        prev.map((c) => (c.id === id ? { ...c, joined: false } : c))
      );
    } catch (e) {
      console.error("❌ Error leaving community:", e);
    }
  };

  const handleOpenChat = async (community) => {
    try {
      setLoading(true);
      
      const res = await fetch(
        `${API_BASE}${endpoints.getMessagingCommunityMessages}${community.id}/`,
        { headers }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : data.results || []);
      setSelectedCommunity(community);
      setView("chat");
    } catch (e) {
      console.error("❌ Error fetching community messages:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedCommunity) return;
    try {
      const res = await fetch(`${API_BASE}${endpoints.postMessagingCommunityPost}`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          community: selectedCommunity.id,
          text: messageText,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), text: messageText, self: true },
      ]);
      setMessageText("");
    } catch (e) {
      console.error("❌ Error sending message:", e);
    }
  };

  const switchToExplore = () => {
    setView("explore");
  };

  const switchToJoined = () => {
    setView("joined");
  };

  return (
    <div className="flex flex-col h-full w-full bg-[#F7F8FA] rounded-xl overflow-hidden">
      <AnimatePresence mode="wait">
        {view === "welcome" && (
          <motion.div
            key="welcome"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center justify-center flex-1 text-center p-10"
          >
            <h2 className="text-3xl font-bold text-[#302B2B] mb-3">
              Join community
              <br />
              <span className="text-[#5563F6]">by your niche</span>
            </h2>

            <p className="text-gray-500 max-w-md mb-10">
              Connect with people who share your interests and passions.
              Explore communities tailored to your niche.
            </p>

            <div className="relative w-72 h-72 mb-10">
              <motion.img
                src={chat_explore}
                alt="center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.8 }}
              />
            </div>

            <motion.button
              onClick={handleExplore}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-[#5563F6] text-white px-8 py-3 rounded-full font-semibold shadow-md hover:bg-[#4450d6] transition"
            >
              Explore community
            </motion.button>
          </motion.div>
        )}

        {view === "explore" && (
          <motion.div
            key="explore"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="p-6 overflow-y-auto flex-1"
          >
            {loading ? (
              <p className="text-gray-400 text-center mt-20">Loading...</p>
            ) : (
              <div className="grid grid-cols-2 gap-6">
                {communities.map((c) => (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition bg-white"
                  >
                    <div className="h-[160px] bg-gray-100 flex items-center justify-center overflow-hidden">
                      <img
                        src={c.icon || "/community/default.jpg"}
                        alt={c.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4 flex flex-col justify-between h-[200px]">
                      <div>
                        <h4 className="font-semibold text-[#302B2B] mb-1 truncate">{c.name}</h4>
                        <p className="text-sm text-[#9A99AB] line-clamp-3 mb-3">{c.description || "No description"}</p>
                        <p className="text-xs text-[#9A99AB]">{c.joined_count || 0} members</p>
                      </div>
                      <button
                        onClick={() => (!c.joined ? handleJoin(c.id) : handleOpenChat(c))}
                        disabled={c.joined}
                        className={`mt-3 w-full py-2 rounded-full text-sm font-medium transition ${c.joined
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-[#5563F6] text-white hover:bg-[#4450d6]"
                          }`}
                      >
                        {c.joined ? "Joined" : "Join Community"}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {view === "joined" && (
          <div className="p-6 grid grid-cols-2 gap-6 overflow-y-auto">
            {joinedCommunities.map((c) => (
              <div key={c.id} className="border border-gray-100 rounded-2xl shadow-sm bg-white transition">
                <div className="h-[160px] bg-gray-100 flex items-center justify-center overflow-hidden">
                  <img src={c.icon || "/community/default.jpg"} alt={c.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-4 flex flex-col justify-between h-[200px]">
                  <div>
                    <h4 className="font-semibold text-[#302B2B] mb-1 truncate">{c.name}</h4>
                    <p className="text-sm text-[#9A99AB] line-clamp-3 mb-3">{c.description || "No description"}</p>
                    <p className="text-xs text-[#9A99AB]">{c.joined_count || 0} members</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenChat(c)}
                      className="flex-1 bg-[#5563F6] text-white py-2 rounded-full text-sm font-medium hover:bg-[#4450d6]"
                    >
                      Open Chat
                    </button>
                    <button
                      onClick={() => handleLeave(c.id)}
                      className="flex-1 bg-red-500 text-white py-2 rounded-full text-sm font-medium hover:bg-red-600"
                    >
                      Leave
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {view === "chat" && selectedCommunity && (
          <div className="flex flex-col h-full border rounded-xl bg-white">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-lg">{selectedCommunity.name}</h3>
              <button
                onClick={switchToJoined}
                className="text-sm text-[#5563F6] hover:underline"
              >
                ← Back
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`p-3 rounded-xl max-w-[70%] ${m.self ? "ml-auto bg-[#5563F6] text-white" : "bg-gray-100 text-gray-800"
                    }`}
                >
                  {m.text}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div className="p-3 border-t flex gap-2">
              <input
                type="text"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Type a message..."
                className="flex-1 border rounded-full px-4 py-2 outline-none focus:ring-2 focus:ring-[#5563F6]"
              />
              <button
                onClick={handleSendMessage}
                className="bg-[#5563F6] text-white px-5 rounded-full hover:bg-[#4450d6]"
              >
                Send
              </button>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}